// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";

// Types
import {
  OptionalExamTogglesType,
  SelectedExamCardType,
} from "src/services/exams/types";

interface CustomSwitchInputProps extends React.HTMLAttributes<HTMLDivElement> {
  "data-testid"?: string;
}

type ToggleGridType = {
  title: string;
  tooltip: string;
  checked: boolean;
  disabled: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  testId?: string;
};

const ToggleGrid = ({
  title,
  tooltip,
  checked,
  disabled,
  onChange,
  testId = undefined,
}: ToggleGridType) => {
  const textStyle = {
    fontSize: "12px",
    fontWeight: "normal",
    color: "#FFF",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "105px",
  };
  const tooltipStyle = {
    fontSize: "12px",
    fontWeight: "normal",
    color: "#FFF",
  };

  return (
    <FormControlLabel
      sx={{
        marginRight: 0,
        marginLeft: 0,
        height: 0,
        "& .MuiFormControlLabel-label": {
          marginLeft: "0px",
        },
      }}
      control={
        <CustomSwitch
          small
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          inputProps={{ "data-testid": testId } as CustomSwitchInputProps}
        />
      }
      label={
        <TitleWithTooltip
          title={title}
          tooltip={tooltip}
          titleStyle={textStyle}
          tooltipStyle={tooltipStyle}
        />
      }
    />
  );
};

type ExamCardTogglesType = {
  optionalToggles: OptionalExamTogglesType;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    optionalToggles: OptionalExamTogglesType
  ) => void;
  showToggles?: OptionalExamTogglesType;
  disableToggles?: OptionalExamTogglesType;
};

const ExamCardToggles = ({
  optionalToggles,
  updateExamCardProperty,
  showToggles = {},
  disableToggles = {},
}: ExamCardTogglesType) => {
  const { t } = useTranslation();

  const {
    autoPause,
    calibration,
    encouragement,
    requiresTutorial,
    unlimitedTime,
    showProgressRing,
  } = optionalToggles;

  return (
    <Grid display="flex" flexWrap="wrap" height="72px">
      {/* Auto-pause */}
      {showToggles.autoPause !== undefined && (
        <Grid item xs={6}>
          <ToggleGrid
            title={t("Auto-pause")}
            tooltip={t(
              "Automatically pauses the exam if the patient is not focused."
            )}
            disabled={disableToggles.autoPause ?? false}
            checked={autoPause ?? false}
            onChange={(e) =>
              updateExamCardProperty("optionalToggles", {
                ...optionalToggles,
                autoPause: e.target.checked,
              })
            }
          />
        </Grid>
      )}

      {/* Encouragement */}
      {showToggles.encouragement !== undefined && (
        <Grid item xs={6}>
          <ToggleGrid
            title={t("Encouragement")}
            tooltip={t("Selects the encouragement feature.")}
            disabled={disableToggles.encouragement ?? false}
            checked={encouragement ?? false}
            onChange={(e) =>
              updateExamCardProperty("optionalToggles", {
                ...optionalToggles,
                encouragement: e.target.checked,
              })
            }
          />
        </Grid>
      )}

      {/* Calibration */}
      {showToggles.calibration !== undefined && (
        <Grid item xs={6}>
          <ToggleGrid
            title={t("Calibration")}
            tooltip={t("Calibrates the patient's headset.")}
            disabled={disableToggles.calibration ?? false}
            checked={calibration ?? false}
            onChange={(e) =>
              updateExamCardProperty("optionalToggles", {
                ...optionalToggles,
                calibration: e.target.checked,
              })
            }
          />
        </Grid>
      )}

      {/* Tutorial */}
      {showToggles.requiresTutorial !== undefined && (
        <Grid item xs={6}>
          <ToggleGrid
            title={t("Tutorial")}
            tooltip={t("Runs a guided tutorial in the patient's language.")}
            disabled={disableToggles.requiresTutorial ?? false}
            checked={requiresTutorial ?? false}
            onChange={(e) =>
              updateExamCardProperty("optionalToggles", {
                ...optionalToggles,
                requiresTutorial: e.target.checked,
              })
            }
            testId="toggle-grid-tutorial"
          />
        </Grid>
      )}

      {/* Unlimited Time */}
      {showToggles.unlimitedTime !== undefined && (
        <Grid item xs={6}>
          <ToggleGrid
            title={t("Unlimited Time")}
            tooltip={t(
              "Gives the patient unlimited time to complete the exam."
            )}
            disabled={disableToggles.unlimitedTime ?? false}
            checked={unlimitedTime ?? false}
            onChange={(e) =>
              updateExamCardProperty("optionalToggles", {
                ...optionalToggles,
                unlimitedTime: e.target.checked,
              })
            }
            testId="toggle-grid-unlimited-time"
          />
        </Grid>
      )}

      {/* Progress Ring */}
      {showToggles.showProgressRing !== undefined && (
        <Grid item xs={6}>
          <ToggleGrid
            title={t("Progress Ring")}
            tooltip={t("Displays or hides the progress ring on the exam.")}
            disabled={disableToggles.showProgressRing ?? false}
            checked={showProgressRing ?? false}
            onChange={(e) =>
              updateExamCardProperty("optionalToggles", {
                ...optionalToggles,
                showProgressRing: e.target.checked,
              })
            }
            testId="toggle-grid-show-progress-ring"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ExamCardToggles;
